/* global Liferay */
import React, { useState, useEffect } from 'react';

const FALLBACK_MDF_REQUESTS = [
  {
    id: 1,
    name: "Spring European Adventure Tour",
    dealerAccount: "Berlin BMW Motorrad",
    requestedAmount: 12500,
    approvedAmount: 10000,
    mDFStatus: "Approved",
    reason: "A cross-country adventure tour across the Alps designed to showcase the resilience of the TR500 luggage system on BMW GS models. Expected reach: 15,000 riders via social media coverage and regional meetups."
  },
  {
    id: 2,
    name: "Summer Solara Gear Promotion",
    dealerAccount: "Munich Moto Center",
    requestedAmount: 8000,
    approvedAmount: 8000,
    mDFStatus: "Approved",
    reason: "Local dealership catalog, billboards and social media marketing push for Solara's new Summer technical gloves and light adventure jackets."
  },
  {
    id: 3,
    name: "Dealer Banner Campaign",
    dealerAccount: "Solara Barcelona Dealer",
    requestedAmount: 3500,
    approvedAmount: 0,
    mDFStatus: "Submitted",
    reason: "Request for in-store display banners, flags and point-of-sale printed materials for the brand new Solara TR500 Aluminium Luggage Case line-up."
  },
  {
    id: 4,
    name: "Winter Rider Safety Workshops",
    dealerAccount: "Hamburg Trail Gear",
    requestedAmount: 6000,
    approvedAmount: 0,
    mDFStatus: "Rejected",
    reason: "Local dealership rider training sessions in the snow. Rejected due to conflict of timing with the primary summer campaign budgets."
  }
];

const getMDFStatusKey = (mdfStatus) => {
  if (!mdfStatus) return 'submitted';
  if (typeof mdfStatus === 'string') {
    // Convert spaces to dashes (e.g. "In Review" -> "in-review") for clean CSS class mapping
    return mdfStatus.toLowerCase().replace(/\s+/g, '-');
  }
  if (typeof mdfStatus === 'object' && mdfStatus.key) {
    return mdfStatus.key.toLowerCase().replace(/\s+/g, '-');
  }
  return 'submitted';
};

const getMDFStatusLabel = (mdfStatus) => {
  if (!mdfStatus) return 'Submitted';
  if (typeof mdfStatus === 'string') return mdfStatus;
  if (typeof mdfStatus === 'object' && mdfStatus.name) return mdfStatus.name;
  if (typeof mdfStatus === 'object' && mdfStatus.key) return mdfStatus.key;
  return 'Submitted';
};

const getDealerName = (dealerAccount) => {
  if (!dealerAccount) return 'N/A';
  if (typeof dealerAccount === 'string') return dealerAccount;
  if (typeof dealerAccount === 'object' && dealerAccount.name) return dealerAccount.name;
  if (typeof dealerAccount === 'object' && dealerAccount.key) return dealerAccount.key;
  return 'N/A';
};

const getMDFDetailUrl = (mdf) => {
  // 1. Resolve dynamic browser origin, fallback to standard if offline
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : 'https://solaramoto.com';
  
  // 2. Base Site URL is origin + Liferay site path '/web/dealers'
  const baseSiteUrl = `${origin}/web/dealers`;
  const erc = mdf.externalReferenceCode || 'N/A';
  
  // 3. Construct URL (with the fixed 88150 DPT layout identifier)
  let url = `${baseSiteUrl}/e/mdf-detail/88150/${erc}`;
  
  // 4. Pattern check (Verification and Auto-correction)
  const pattern = /https?:\/\/[^\/]+\/web\/dealers\/e\/mdf-detail\/88150\/[A-Za-z0-9_-]+/;
  if (!pattern.test(url)) {
    console.warn(`[MDF-URL-VALIDATION] Formed URL does not match pattern: "${url}". Auto-correcting...`);
    url = `https://solaramoto.com/web/dealers/e/mdf-detail/88150/${erc}`;
  } else {
    console.log(`[MDF-URL-VALIDATION] Validated URL successfully formed: "${url}"`);
  }
  
  return url;
};

export default function App() {
  const [mdfRequests, setMdfRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMdfRequests();
  }, []);

  const fetchMdfRequests = async () => {
    setLoading(true);
    setError(null);

    let activeAccountId = null;
    let activeAccountName = null;

    // 1. Retrieve the logged-in user securely via "my-user-account" (accessible to standard dealers)
    if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay && Liferay.ThemeDisplay.isSignedIn()) {
      try {
        const userRes = await Liferay.Util.fetch(`/o/headless-admin-user/v1.0/my-user-account`);
        if (userRes.ok) {
          const userData = await userRes.json();
          if (userData && userData.accountBriefs && userData.accountBriefs.length > 0) {
            // Dealers typically belong to a single, principal account
            const primaryAccount = userData.accountBriefs[0];
            activeAccountId = String(primaryAccount.id);
            activeAccountName = primaryAccount.name;
            console.log('Logged-in Dealer Account:', activeAccountName, `(ID: ${activeAccountId})`);
          }
        }
      } catch (e) {
        console.warn('Failed to retrieve user accounts, bypassing account filtering:', e.message);
      }
    }

    // 2. Fetch the MDF Requests (Using server-side OData filtering for maximum performance!)
    if (typeof Liferay !== 'undefined' && Liferay.Util && Liferay.Util.fetch) {
      try {
        let apiUrl = '/o/c/mdfrequests?pageSize=100';
        
        // If an active dealer account is associated, filter on the server-side directly!
        if (activeAccountId) {
          apiUrl = `/o/c/mdfrequests?filter=dealerAccount eq '${activeAccountId}'&pageSize=100`;
          console.log(`Applying server-side OData filter: ${apiUrl}`);
        }

        const response = await Liferay.Util.fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch MDF Requests. Status: ${response.status}`);
        }
        const data = await response.json();
        const rawItems = data && data.items ? data.items : (Array.isArray(data) ? data : []);
        setMdfRequests(rawItems);
      } catch (err) {
        console.warn('Liferay.Util.fetch failed, loading local fallback mock data:', err.message);
        setMdfRequests(FALLBACK_MDF_REQUESTS);
      } finally {
        setLoading(false);
      }
    } else {
      // Offline/Local dev fallback
      console.log('Liferay environment not detected. Loading local fallback mock MDF Requests.');
      setTimeout(() => {
        setMdfRequests(FALLBACK_MDF_REQUESTS);
        setLoading(false);
      }, 500);
    }
  };

  if (loading) {
    return (
      <div className="solara-mdf-widget p-4 text-center">
        <div className="spinner-border text-warning" role="status">
          <span className="sr-only">Loading MDF Requests...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="solara-mdf-widget py-2">
      {/* Widget Header (Minimalist right-aligned Refresh button) */}
      <div className="d-flex justify-content-end align-items-center mb-3">
        <button className="btn btn-solara" onClick={fetchMdfRequests} style={{ padding: '8px 16px', fontSize: '12px' }}>
          Refresh Feed
        </button>
      </div>

      {/* Grid of MDF Cards */}
      <div className="row g-4">
        {mdfRequests.length === 0 ? (
          <div className="col-12 text-center text-muted py-5">
            No Marketing Development Fund requests found.
          </div>
        ) : (
          mdfRequests.map((mdf) => {
            const statusKey = getMDFStatusKey(mdf.mDFStatus);
            const statusClass = `status-${statusKey}`;
            
            // Format amounts nicely
            const requestedFormatted = typeof mdf.requestedAmount === 'number' 
              ? `$${mdf.requestedAmount.toLocaleString()}` 
              : mdf.requestedAmount || '$0';

            const approvedFormatted = typeof mdf.approvedAmount === 'number' 
              ? `$${mdf.approvedAmount.toLocaleString()}` 
              : mdf.approvedAmount || '$0';

            // Generate validated detail page link based on the exact Figma path spec
            const detailUrl = getMDFDetailUrl(mdf);

            return (
              <div key={mdf.externalReferenceCode || mdf.id || mdf.name} className="col-md-6 col-lg-4 mb-4" style={{ padding: '12px' }}>
                <div className="solara-mdf-card h-100">
                  <div className="mdf-card-wrapper" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    
                    {/* Top Row: Badge & Status */}
                    <div className="card-header-row mb-3 d-flex justify-content-between align-items-center">
                      <span className={`status-badge ${statusClass}`}>
                        {getMDFStatusLabel(mdf.mDFStatus)}
                      </span>
                      <span className="text-muted text-uppercase font-semibold tracking-wider" style={{ fontSize: '10px' }}>
                        MDF Request
                      </span>
                    </div>

                    {/* Title (Clickable Link!) */}
                    <h3 className="card-title">
                      <a href={detailUrl} className="card-link-title" style={{ color: '#1D1C21', textDecoration: 'none', fontFamily: 'Barlow Semi Condensed, sans-serif', fontSize: '20px', fontWeight: '700' }}>
                        {mdf.name}
                      </a>
                    </h3>
                    <div className="card-dealer mb-3 text-muted" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: 'rgba(29, 28, 33, 0.6)' }}>
                      <strong>Dealer:</strong> {getDealerName(mdf.dealerAccount)}
                    </div>

                    {/* Short Reason / Description */}
                    <p className="card-short-desc text-muted mb-4" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: 'rgba(29, 28, 33, 0.7)', lineHeight: '1.5' }}>
                      {mdf.reason ? (
                        mdf.reason.length > 120 
                          ? `${mdf.reason.substring(0, 120)}...` 
                          : mdf.reason
                      ) : 'No reason provided.'}
                    </p>

                    {/* Metrics Grid */}
                    <div className="card-metrics mt-auto mb-3" style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <div className="row g-2">
                        <div className="col-6 border-right">
                          <div className="metric-label" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: '700', color: 'rgba(29, 28, 33, 0.4)', textTransform: 'uppercase' }}>Requested</div>
                          <div className="metric-value text-dark" style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: '600', color: '#1D1C21' }}>
                            {requestedFormatted}
                          </div>
                        </div>
                        <div className="col-6" style={{ paddingLeft: '1rem' }}>
                          <div className="metric-label" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: '700', color: 'rgba(29, 28, 33, 0.4)', textTransform: 'uppercase' }}>Approved</div>
                          <div className="metric-value highlight" style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-primary, #F7A01C)' }}>
                            {approvedFormatted}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* View Request Action Link */}
                    <div className="pt-2 border-top" style={{ paddingTop: '0.5rem', borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <a href={detailUrl} className="btn-view-details" style={{ color: 'var(--color-brand-primary, #F7A01C)', textDecoration: 'none', textTransform: 'uppercase', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        View Full Request
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </a>
                    </div>

                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}